<template>
  <MessageScreen>
    <div class="loading-screen" v-if="errorLoading">
      <span>{{ errorLoadingMessage }}</span>
      <RoundedButton 
        text="Go back" 
        shadow 
        :enterPress="() => goBack" 
        @click="goBack" 
        class="bottom" 
        ariaLabel="Go back"  
      />
    </div>
    <div class="loading-screen" v-else>
      <span>Loading...</span>
      <ProgressSpinner class="spinner bottom" />
    </div>
  </MessageScreen>
</template>

<script>
import MessageScreen from "../components/MessageScreen.vue";
import RoundedButton from "../components/RoundedButton.vue";
import { dbConstants } from "../assets/constants";
import { toTitleCase } from "../assets/helpers_legacy";
import { makeMPServerRequest } from "../assets/helpers.ts";

export default {
  components: {
    MessageScreen,
    RoundedButton,
  },

  data() {
    return {
      errorLoading: false,
      errorLoadingMessage: "",
      bookingType: "",
      params: {
        id: this.$store.state.user.sessionDetails.id,
      },
    };
  },

  async mounted() {
    if (
      this.$store.state.user.currentUser.type.toLowerCase().trim() === "student" ||
      this.$store.state.user.currentUser.type.toLowerCase().trim() === "normal" ||
      this.$store.state.user.currentUser.type.toLowerCase().trim() === "personal" ||
      this.$store.state.user.currentUser.type.toLowerCase().trim() === "supervisor" ||
      this.$store.state.user.currentUser.type.toLowerCase().trim() === "org user" ||
      this.$store.state.user.currentUser.type.toLowerCase().trim() === "owner"
    ) {
      const parsedData = this.$store.state.user.sessionDetails;
      if (!parsedData.uploads || parsedData.uploads.length === 0) {
        console.log("No uploads found for session");
        this.errorLoadingMessage = "Session is not ready for viewing yet";
        this.errorLoading = true;
        return;
      }

      const getHeaders = new Headers();
      getHeaders.append("content-type", "application/json");
      getHeaders.append(
        "Authorization",
        `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
      );
      const getRequestOptions = {
        method: "GET",
        headers: getHeaders,
        redirect: "follow",
      };

      const mediaUploadData = parsedData.uploads.find(
        (upload) => upload.type !== "mp-supplementary"
      );

      let voiceId = this.$store.state.user.ttsVoiceBySession[parsedData.id];
      if (!voiceId) {
        this.$store.commit("user/setTTSVoiceBySession", {
          sessionId: parsedData.id,
          voiceId: "default"
        });

        voiceId = "default";
      }

      if (
        !parsedData.uploads ||
        mediaUploadData.type === "mp-instant-session"
      ) {
        // .uploads should be undefined only for instant sessions without a recording
        // Not sure if there's a better way to determine this atm
        this.$store.commit("user/setSessionDetails", {
          ...this.$store.state.user.sessionDetails,
          isInstant: true,
          mediaUploadData: mediaUploadData,
        });

        this.$router.push({
          name: "Captioner",
        });
      } else if (mediaUploadData.type === "mp-document") {
        // Get a presigned url for the document from mp-server
        fetch(
          process.env.VUE_APP_MP_SERVER_API_URL.concat(
            "self/uploads/",
            mediaUploadData.id + "/get-presigned-url"
          ),
          getRequestOptions
        )
          .then((response) => response.text())
          .then((result) => {
            let data = JSON.parse(result);

            this.$store.commit("user/setSessionDetails", {
              ...this.$store.state.user.sessionDetails,
              documentUrl: data.data,
              sessionType: "document",
              mediaUploadData: mediaUploadData,
            });

            this.$router.push({
              name: "NoteTaker",
            });
          });
      } else if (mediaUploadData.type === "mp-video") {
        if (!mediaUploadData.id) {
          this.errorLoadingMessage = "Session is not ready for viewing yet";
          this.errorLoading = true;
          return;
        }

        await this.getTranscriptBasedOnVoice(
          getRequestOptions,
          voiceId,
          mediaUploadData,
          'video'
        );
      } else {
        if (!mediaUploadData.id) {
          this.errorLoadingMessage = "Session is not ready for viewing yet";
          this.errorLoading = true;
          return;
        }

        await this.getTranscriptBasedOnVoice(
          getRequestOptions,
          voiceId,
          mediaUploadData,
          'recording'
        );
      }
    } else {
      new Promise((resolve, reject) => {
        var myHeaders = new Headers();
        myHeaders.append("content-type", "application/json");
        myHeaders.append(
          "x-hasura-admin-secret",
          process.env.VUE_APP_HASURA_ADMIN_SECRET
        );
        var graphql = JSON.stringify({
          query: `query GetBookingInfo($id: uuid!) {
                    calendar_tasks_by_pk(id: $id) {
                      start_time,
                      end_time,
                      tz,
                      name,
                      service_type,
                      labels,
                      notetaking_tasks {
                        id
                      }
                    }
                    booking_labels(where: {calendar_tasks: {id: {_eq: $id}}}) {
                      id
                      course_crn
                      course_name
                      booking_labels_keywords {
                        content
                        phrases
                      }
                    }
                  }
                `,
          variables: {
            id: this.$store.state.user.sessionDetails.id,
          },
        });

        var requestOptions = {
          method: "POST",
          headers: myHeaders,
          body: graphql,
          redirect: "follow",
        };
        fetch(process.env.VUE_APP_HASURA_URL, requestOptions)
          .then((response) => response.text())
          .then((result) => {
            let res = JSON.parse(result);
            const noBooking = "Booking not found";
            const table = "calendar_tasks_by_pk";
            const bookingLabels = "booking_labels";

            if (res.errors) {
              if (
                res.errors[0].message.includes(
                  "invalid input syntax for type uuid"
                )
              ) {
                reject(noBooking);
              } else {
                reject("Received error: " + res.errors[0].message);
              }
            } else if (res.data[table]) {
              if (res.data[table].notetaking_tasks.length > 0) {
                this.params.bundledNoteTakingTask =
                  res.data[table].notetaking_tasks[0];
              }
              this.bookingType = res.data[table].service_type;
              this.params.bookingName =
                (res.data[table].labels["Course Code"]
                  ? res.data[table].labels["Course Code"] + ": "
                  : "") + toTitleCase(res.data[table].name);

              let startDate = new Date(res.data[table].start_time);
              let endDate = new Date(res.data[table].end_time);

              let startTime = res.data[table].start_time.substr(11, 5);
              // Strip leading 0
              if (startTime[0] === "0") {
                startTime = startTime.substring(1);
              }

              let endTime = res.data[table].end_time.substr(11, 5);
              // Strip leading 0
              if (endTime[0] === "0") {
                endTime = endTime.substring(1);
              }

              this.params.bookingEndTime = endTime;

              if (
                res.data[bookingLabels][0].booking_labels_keywords &&
                res.data[bookingLabels][0].booking_labels_keywords.length > 0
              ) {
                this.params.bookingLabelsKeywords =
                  res.data[bookingLabels][0].booking_labels_keywords[0].content;
                this.params.bookingLabelsKeywordsDictionary =
                  JSON.stringify(
                    res.data[bookingLabels][0].booking_labels_keywords[0]
                      .phrases
                  ) || JSON.stringify({ phrases: [] });
              }
              this.params.bookingLabelId = res.data[bookingLabels][0].id;
              const split = res.data[table].tz.split("/");
              if (split.length === 2) {
                this.bookingTimezone = split[1];
              } else {
                this.bookingTimezone = res.data[table].tz;
              }
              this.params.bookingTimezone = this.bookingTimezone.replaceAll(
                "_",
                " "
              );
              // Remove class that disables animations on page load
              setTimeout(() => {
                this.disableTransitions = false;
              }, 500);

              // Booking type specific settings
              // Captioning booking
              //   if (serviceType === dbConstants.SERVICE_TYPE_CAPTIONING) {
              this.params.bookingStartTime = startTime;
              //   }
              // Note-taking booking
              //   else {
              this.params.turnaroundTime = (endDate - startDate) / 3.6e6;
              this.params.bookingEndDate = endDate.toLocaleString("default", {
                year: "numeric",
                month: "long",
                day: "numeric",
              });

              resolve("Got booking info");
            } else {
              /**
               * Couldn't find booking in calendar_tasks table, so check notetaking_tasks table
               */
              var noteTakingGraphql = JSON.stringify({
                query: `query GetNoteTakingInfo($id: uuid!) {
                    notetaking_tasks_by_pk(id: $id) {
                      calendar_tasks_id
                      media_name
                      deadline
                      tz
                      notetaking_tasks_files {
                        anthill_task_id
                      }
                    }
                  }
                `,
                variables: {
                  id: this.$store.state.user.sessionDetails.id,
                },
              });

              requestOptions.body = noteTakingGraphql;

              fetch(process.env.VUE_APP_HASURA_URL, requestOptions)
                .then((response) => response.text())
                .then((result) => {
                  let res = JSON.parse(result);

                  if (res.errors) {
                    if (
                      res.errors[0].message.includes(
                        "invalid input syntax for type uuid"
                      )
                    ) {
                      reject(noBooking);
                    } else {
                      reject("Received error: " + res.errors[0].message);
                    }
                  } else if (res.data.notetaking_tasks_by_pk) {
                    this.bookingType = dbConstants.SERVICE_TYPE_NOTETAKING;
                    this.params.bundledCaptioningId =
                      res.data.notetaking_tasks_by_pk.calendar_tasks_id;
                    this.params.mediaName =
                      res.data.notetaking_tasks_by_pk.media_name;
                    this.params.deadline =
                      res.data.notetaking_tasks_by_pk.deadline;
                    this.params.timezone = res.data.notetaking_tasks_by_pk.tz;
                    this.params.anthillTranscriptId = res.data
                      .notetaking_tasks_by_pk.notetaking_tasks_files[0]
                      ? res.data.notetaking_tasks_by_pk
                          .notetaking_tasks_files[0].anthill_task_id
                      : null;
                    resolve("Got notetaking booking info");
                  } else {
                    reject(noBooking);
                  }
                });
            }
          })
          .catch(() => {
            reject("Error retrieving booking information");
          });
      })
        .then(() => {
          this.$store.commit("user/setSessionDetails", {
            ...this.$store.state.user.sessionDetails,
            ...this.params,
          });
          if (this.bookingType === dbConstants.SERVICE_TYPE_CAPTIONING) {
            console.dir(this.params);
            this.$router.push({
              name: "Captioner",
            });
          } else if (this.bookingType === dbConstants.SERVICE_TYPE_NOTETAKING) {
            this.$router.push({
              name: "NoteTaker",
            });
          }
        })
        .catch((errorMsg) => {
          this.errorLoading = true;
          this.errorLoadingMessage = errorMsg;
        });
    }
  },

  methods: {
    goBack() {
      this.$router.push("/home");
    },

    async getTranscriptBasedOnVoice(request, voiceId, mediaUploadData, sessionType) {
      fetch(
        process.env.VUE_APP_MP_SERVER_API_URL.concat(
          `self/uploads/${mediaUploadData.id}/voices/${voiceId}`
        ),
        request
      )
      .then((response) => response.text())
      .then((result) => {
        let data = JSON.parse(result);

        if (data.status === 'processing') {
          // If voice and transcript not yet ready
          if (voiceId !== 'default') {
            this.$store.commit("user/setSessionDetails", {
              ...this.$store.state.user.sessionDetails,
              isProcessing: true,
              sessionType: sessionType,
              mediaUploadData: mediaUploadData,
            });

            this.$router.push({
              name: 'NoteTaker'
            });
          } else {
            // If voice is default, set error message
            this.errorLoading = true;
            this.errorLoadingMessage = "Session is not ready for viewing yet";
          }
        } else {
          // If voice and transcript ready
          this.$store.commit("user/setSessionDetails", {
            ...this.$store.state.user.sessionDetails,
            isProcessing: false,
            uploadDetails: data.data,
            sessionType: sessionType,
            mediaUploadData: mediaUploadData,
            transcriptData: this.convertWordsToSentences(data.data.alignmentJson),
          });

          let apiUrl = `self/uploads/${mediaUploadData.id}`;
          if (voiceId !== "default") {
            apiUrl = `self/voices/${data.data.id}`;
          }

          this.getAudioPresignedUrl(apiUrl, sessionType);
        }
      })
      .catch((error) => {
        console.log(error);
      });
    },

    getAudioPresignedUrl(apiUrl, sessionType) {
      makeMPServerRequest(
        `${apiUrl}/get-presigned-url`,
        'GET',
        'json',
        null
      )
      .then((response) => {
        this.$store.commit("user/setSessionDetails", {
          ...this.$store.state.user.sessionDetails,
          audioUrl: sessionType === 'recording' ? response.data : null,
          videoUrl: sessionType === 'video' ? response.data : null,
        });

        this.$router.push({
          name: 'NoteTaker'
        });
      })
      .catch((error) => {
        console.log(error);
        this.errorLoading = true;
        this.errorLoadingMessage = `Error loading ${sessionType}`;
      });
    },

    convertWordsToSentences(transcript) {
      const sentences = [];
      const rawData = [];
      let sentence = '';
      let currentSentence = null;
      let currentIndex = 0;
      let currentSpeaker = transcript[0].speaker_id;

      transcript.forEach((item, index) => {
        if (!currentSentence) {
          // If current sentence is not initialized, initialize it
          currentSentence = {
            words: [],
            sentence: "",
            startTimestamp: Math.round(item.start * 1000),
            endTimestamp: -1,
            startIndex: index,
            sentenceIndex: currentIndex,
            endIndex: -1,
            speakerId: null,
          };

          if (item.speaker_id) {
            currentSentence.speakerId = item.speaker_id;
          }
        }

        const word = {
          wordIndex: index,
          word: item,
          sentenceIndex: currentIndex,
        };

        currentSentence.words.push(word);
        rawData.push(word);

        const speakerWillChange = transcript[index + 1] && transcript[index + 1].speaker_id && transcript[index + 1].speaker_id !== currentSpeaker;

        if (
          (
            item.text.charAt(item.text.length - 1) === '.' &&
            item.text.charAt(item.text.length - 2) !== '.'
          ) &&
          currentSentence.words.length >= 20 ||
          speakerWillChange
        ) {
          // If last word in the sentence add without space
          sentence += item.text;
        } else {
          // Otherwise add with space
          sentence += item.text + ' ';
        }

        if (index === transcript.length - 1) {
          // If last word in the transcript initialise sentence object and add it to array
          currentSentence.endTimestamp = Math.round(item.end * 1000);
          currentSentence.endIndex = index;
          currentSentence.sentence = sentence;
          sentences.push(currentSentence);
        } else if (
          (
            item.text.charAt(item.text.length - 1) === '.' &&
            item.text.charAt(item.text.length - 2) !== '.'
          ) &&
          currentSentence.words.length >= 20 ||
          speakerWillChange
        ) {
          // If last word in the sentence initialise sentence object, add it to array and reset current sentence
          currentSentence.endTimestamp = Math.round(item.end * 1000);
          currentSentence.endIndex = index;
          currentSentence.sentence = sentence;
          sentences.push(currentSentence);
          currentSentence = null;
          sentence = '';
          currentIndex += 1;
          currentSpeaker = transcript[index + 1].speaker_id ? transcript[index + 1].speaker_id : null;
        }
      });

      return {
        sentences,
        rawData,
      }
    },
  },
};
</script>

<style></style>
